import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { Icon } from '@/icons/Icon'

const variants = cva(
  'shrink-0 w-fit flex font-medium justify-center items-center whitespace-nowrap rounded-full cursor-default leading-snug backdrop-blur-md h-6 text-13 md:h-5 md:text-12',
  {
    variants: {
      style: {
        default:
          'border border-gray-300 bg-gray-100 text-gray-500 dark:border-white/10 dark:text-zinc-300 dark:bg-white/5',
        primary:
          'border border-primary-500/20 bg-primary-500/5 text-primary-500 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-500',
        secondary:
          'bg-primary-50 text-primary-500 border border-primary-500/5 dark:border-white/15 dark:text-zinc-300 dark:bg-black',
        capable:
          'bg-amber-50 text-amber-800 border border-amber-300 dark:bg-amber-300/10 dark:border-amber-300/20 dark:text-amber-300',
        outline:
          'bg-white border border-gray-200 text-gray-500 dark:bg-black dark:border-white/15 dark:text-zinc-400',
        success: 'bg-success-500/10 text-success-500 border border-success-500/20',
        subtle:
          'border border-black/[0.06] bg-black/[0.03] text-gray-500 dark:border-white/[0.06] dark:text-zinc-300 dark:bg-white/[0.03]',
        disabled: 'bg-gray-200 text-gray-500 border border-gray-300',
      },
      rounded: {
        true: 'rounded-full',
        false: 'rounded-md',
      },
    },
    defaultVariants: {
      style: 'default',
      rounded: true,
    },
  },
)

type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof variants> & {
    as?: 'div' | 'span'
    style?:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'capable'
      | 'outline'
      | 'success'
      | 'subtle'
      | 'disabled'
    count?: number | string
    icon?: string
    rounded?: boolean
    nested?: boolean
  }

export default function Badge({
  as: Component = 'div',
  style,
  count,
  icon,
  nested,
  rounded,
  className,
  children,
  ...props
}: BadgeProps) {
  const hasCount = count || count === 0
  const label = hasCount ? count : children
  const iconStroke = hasCount ? 1 : 1.5
  const iconClass = icon === 'best' && style === 'outline' ? 'sm:!hidden' : ''
  const iconSize = icon === 'best' ? 14 : 12
  const countClass = hasCount ? 'gap-1 px-2 md:px-1.5' : 'gap-1.5 px-2.5 md:px-2'

  return (
    <>
      <Component
        className={cn(variants({ style, rounded }), countClass, className)}
        {...props}
        aria-hidden={nested ? true : undefined}
      >
        {icon && (
          <Icon name={icon} size={iconSize} strokeWidth={iconStroke} className={iconClass} />
        )}
        {label}
      </Component>
      {nested && <span className="sr-only">{label}</span>}
    </>
  )
}
